import React, { useEffect } from "react";
import { setSettingsSlot, useSettingsClaims } from "utils/settingsSlots";
import {
    GroupAccent,
    GroupHintSC,
    GroupSC,
    GroupTitleSC,
    RowControlSC,
    RowHintSC,
    RowOffSC,
    RowSC,
    RowTextSC,
    RowTitleSC,
} from "./styled";

interface IGroupProps {
    /** Omitted when something else already heads the band — the «Служебное» collapse does. */
    title?: string;
    hint?: string;
    accent: GroupAccent;
    children: React.ReactNode;
}

/** One category of settings: a titled band of rows. */
export const SettingsGroup = ({ title, hint, accent, children }: IGroupProps) => (
    <GroupSC>
        {title ? <GroupTitleSC $accent={accent}>{title}</GroupTitleSC> : null}
        {hint ? <GroupHintSC>{hint}</GroupHintSC> : null}
        {children}
    </GroupSC>
);

/**
 * The landing place for a button that lives in another component.
 *
 * See utils/settingsSlots: the button stays where its logic is and renders here through a
 * portal, so this is an empty div whose only job is to exist and be found by id. display:contents
 * keeps it out of the layout — the button lines up as if the row held it directly.
 */
const SettingsSlot = ({ id }: { id: string }) => {
    useEffect(() => () => setSettingsSlot(id, null), [id]);
    return (
        <div
            data-settings-slot={id}
            style={{ display: "contents" }}
            ref={(el) => setSettingsSlot(id, el)}
        />
    );
};

interface IRowProps {
    /** What this control is, in the translator's words. */
    title: string;
    /** What pressing it actually does, and to what — folder, language, lecture. */
    hint?: React.ReactNode;
    /** Show a button that lives in a toolbar: the row appears only while that button exists. */
    slot?: string;
    /** For inline controls that only apply sometimes — false hides the row entirely. */
    available?: boolean;
    /** Put the control under the text instead of beside it: for sliders and wide groups. */
    wide?: boolean;
    /** Rebuilds, republishes or throws work away: worth looking at twice. */
    danger?: boolean;
    /**
     * Why the control is (partly) off right now — the «No automatic edits» mode. Said in words
     * under the hint: a tooltip on the button itself never shows on a tablet.
     */
    off?: React.ReactNode;
    children?: React.ReactNode;
}

/**
 * One control, with a name and a plain-language description of what it does.
 *
 * A row is present only when its control is: `slot` rows follow the button's own claim (see
 * utils/settingsSlots), inline rows are gated by `available`. Nothing here inspects the DOM to
 * guess whether something rendered — the component that knows the condition states it.
 */
export const SettingRow = ({
    title,
    hint,
    slot,
    available = true,
    wide = false,
    danger = false,
    off,
    children,
}: IRowProps) => {
    const claims = useSettingsClaims();
    if (!available) return null;
    if (slot && !claims.has(slot)) return null;

    return (
        <RowSC data-setting={title} $wide={wide} $danger={danger}>
            <RowTextSC>
                <RowTitleSC>{title}</RowTitleSC>
                {hint ? <RowHintSC>{hint}</RowHintSC> : null}
                {off ? (
                    <RowOffSC role="note" data-auto-edit-off="">
                        {off}
                    </RowOffSC>
                ) : null}
            </RowTextSC>
            <RowControlSC $wide={wide}>
                {slot ? <SettingsSlot id={slot} /> : null}
                {children}
            </RowControlSC>
        </RowSC>
    );
};
